// My Profile — identity header in the careers.co.jp profile-header shape
// (cover band, overlapping avatar), the signed-in user's own activity, and
// account details. Everything is derived from /api/me + the jobs list; the
// backend has no separate profile endpoint.

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { useMe } from "@/hooks/useMe";
import { STATUS_LABEL, displayNameFromEmail, relativeTime, type JobListItem } from "@/lib/jobs";
import { Badge } from "@/components/ui/badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/ui/stat-card";

export function Profile() {
  const api = useApi();
  const me = useMe();

  const jobsQuery = useQuery({
    queryKey: ["jobs"],
    queryFn: () => api.get<{ jobs: JobListItem[] }>("/api/resume-jobs"),
  });

  if (!me) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-56 w-full rounded-lg" />
        <div className="grid grid-cols-3 gap-3">
          <Skeleton className="h-28 rounded-lg" />
          <Skeleton className="h-28 rounded-lg" />
          <Skeleton className="h-28 rounded-lg" />
        </div>
      </div>
    );
  }

  const name = displayNameFromEmail(me.email) ?? "You";
  const isAdmin = me.role === "admin";
  // The list endpoint scopes visibility already; "mine" means rows I own.
  const mine = (jobsQuery.data?.jobs ?? []).filter((j) => j.ownerEmail === me.email);
  const myDocuments = mine.reduce((sum, j) => sum + j.outputCount, 0);
  const myReady = mine.filter((j) => j.status === "ready_for_review").length;
  const recent = mine.slice(0, 5);

  return (
    <div className="flex flex-col gap-4">
      <section className="relative overflow-hidden rounded-lg border border-border bg-surface shadow-subtle">
        <div aria-hidden className="profile-cover h-28 sm:h-32" />
        <div className="-mt-12 flex flex-col gap-4 px-5 pb-5 sm:flex-row sm:items-end sm:px-7 sm:pb-6">
          <Avatar alt={name} size="2xl" variant="brand" ringed className="shrink-0" />
          <div className="min-w-0 flex-1 sm:pb-1">
            <h1 className="text-3xl leading-tight sm:text-4xl">{name}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-2">
              <span className="truncate">{me.email}</span>
              <span>Ascent Global Partners</span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge variant={isAdmin ? "accent" : "neutral"}>
                {isAdmin ? "Admin" : "Recruiter"}
              </Badge>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-3 gap-3">
        <StatCard label="My resumes" value={jobsQuery.data ? mine.length : "–"} caption="Uploaded by you" />
        <StatCard
          label="In review"
          value={jobsQuery.data ? myReady : "–"}
          tone={myReady > 0 ? "ok" : "default"}
          caption="Ready for your check"
        />
        <StatCard label="Documents" value={jobsQuery.data ? myDocuments : "–"} caption="Generated from your resumes" />
      </section>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Recent activity</CardTitle>
            <Link to="/" className="text-xs text-brand-on no-underline hover:underline">
              All resumes
            </Link>
          </CardHeader>
          <CardBody className="px-0 py-0">
            {jobsQuery.isLoading ? (
              <ul className="divide-y divide-border">
                {Array.from({ length: 3 }).map((_, i) => (
                  <li key={i} className="flex items-center gap-3 px-5 py-3">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-3.5 w-40" />
                      <Skeleton className="h-3 w-56" />
                    </div>
                  </li>
                ))}
              </ul>
            ) : recent.length === 0 ? (
              <p className="px-5 py-6 text-sm text-text-2">
                Nothing yet — resumes you upload will show up here.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {recent.map((job) => {
                  const status = STATUS_LABEL[job.status];
                  return (
                    <li key={job.id} className="transition-colors hover:bg-surface-2">
                      <Link
                        to={`/jobs/${job.id}`}
                        className="flex items-center gap-3 px-5 py-3 no-underline"
                      >
                        {job.candidateName ? (
                          <Avatar alt={job.candidateName} size="sm" />
                        ) : (
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-2 text-text-2">
                            <FileText size={14} strokeWidth={1.75} />
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-text">
                            {job.candidateName ?? job.sourceFilename}
                          </p>
                          <p className="mt-0.5 truncate text-[12px] text-text-2">
                            {relativeTime(job.createdAt)}
                            {job.outputCount > 0 &&
                              ` · ${job.outputCount} document${job.outputCount === 1 ? "" : "s"}`}
                          </p>
                        </div>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
          </CardHeader>
          <CardBody className="flex flex-col gap-3">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-text-2">
                Email
              </p>
              <p className="mt-0.5 text-sm text-text">{me.email}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-text-2">
                Role
              </p>
              <p className="mt-0.5 text-sm text-text">
                {isAdmin ? "Admin — manages templates and reports" : "Recruiter"}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-text-2">
                User ID
              </p>
              <p className="mt-0.5 font-mono text-xs text-text-2">{me.id}</p>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
